import { CspError, CspErrorType, Maybe, StateAssert } from '@csp/csp-common-model';
import {
  GenericRequest,
  GenericRequestMapper,
  ReminderNotificationMetaMapper,
  RequestGroupMapper,
  RequestInitiationConfig,
  RequestInitiationConfigMapper,
  RequestInitiatorType,
  RequestSideEffectConfig,
  RequestSideEffectConfigMapper,
  RequestTagsConfigMetaMapper,
  RpmEventConfigMapper,
  ScheduleCode,
  ScheduleValidationService,
  UnitOfTime,
  VersionCode,
} from '@csp/csp-common-scheduling';

import { QuestionnaireRequestV2, QuestionnaireTimingV2 } from '@csp/dmdp-api-questionnaire-dto';
import {
  PatientCopingTipConfigV1,
  QuestionnaireConsentConfigV1,
  QuestionnaireRequestV1,
  QuestionnaireTimingV1,
} from '@csp/dto';
import { PatientCopingTipMetaMapper } from '../meta/mapper/PatientCopingTipMetaMapper';
import { QuestionnaireConsentConfigMetaMapper } from '../meta/mapper/QuestionnaireConsentConfigMapper';
import { QuestionnaireRequestMetaMapper } from '../meta/mapper/QuestionnaireRequestMetaMapper';
import { QuestionnaireRequestType } from '../type/QuestionnaireRequestType';
import { QuestionnaireType } from '../type/QuestionnaireType';
import { QuestionnaireCriticalityConfigV1 } from '../meta/model/QuestionnaireCriticalityConfigV1';
import { QuestionnaireCriticalityConfigMapper } from '../meta/mapper/QuestionnaireCriticalityConfigMapper';
import { QuestionnaireRequestConfigMeta } from './QuestionnaireRequestConfigMeta';
import { QuestionnaireRequestDetails } from './QuestionnaireRequestDetails';

// Omit meta and represent the meta information via domain types instead, eg QuestionnaireConfigMeta
export type QuestionnaireRequestInfo = GenericRequest<QuestionnaireRequestDetails> & {
  config: QuestionnaireRequestConfigMeta;
  patientCopingTipConfig?: PatientCopingTipConfigV1;
  questionnaireConsentConfig?: QuestionnaireConsentConfigV1;
  questionnaireCode: string;
  requestType: QuestionnaireRequestType;
  sideEffectConfig?: RequestSideEffectConfig;
  questionnaireCriticalityConfig?: QuestionnaireCriticalityConfigV1;
};

const SUPPORTED_UNIT_OF_TIMES = [UnitOfTime.D, UnitOfTime.H, UnitOfTime.MIN];

const assertConfig = (
  requestCode: string,
  config: QuestionnaireRequestConfigMeta,
  timing: QuestionnaireTimingV2 | QuestionnaireTimingV1 | undefined,
): void => {
  if (!config.selfReport && (timing?.repeat?.frequency ?? 0) > 1) {
    throw new CspError(
      CspErrorType.CONFLICT,
      `Questionnaire request timing frequency greater than one is only allowed for self-reporting questionnaires. Request code: ${requestCode}`,
    );
  }
  if (config.selfReport && config.minComplianceThreshold) {
    throw new CspError(
      CspErrorType.CONFLICT,
      `Questionnaire request cannot not have compliance and self-reporting activated at same time . Request code: ${requestCode}`,
    );
  }
};

const evaluateQuestionnaireType = (config: QuestionnaireRequestConfigMeta): QuestionnaireRequestType => {
  if (config.selfReport) {
    return QuestionnaireRequestType.SELF_REPORT;
  } else if (config.minComplianceThreshold) {
    return QuestionnaireRequestType.COMPLIANCE;
  } else {
    return QuestionnaireRequestType.SCHEDULED;
  }
};

const from = (
  scheduleCode: ScheduleCode,
  versionCode: VersionCode,
  {
    requestCode,
    details,
    startAction,
    endAction,
    meta,
    timing,
    while: requestWhile,
  }: QuestionnaireRequestV2 | QuestionnaireRequestV1,
  strict = false,
): QuestionnaireRequestInfo => {
  StateAssert.notNull(details, `Details must be set. RequestCode: ${requestCode}`);
  const config = QuestionnaireRequestMetaMapper.toConfigFromMetaV1(requestCode, meta);
  const patientCopingTipConfig = PatientCopingTipMetaMapper.toPatientCopingTipConfigFromMetaV1(meta);
  const notificationConfig = ReminderNotificationMetaMapper.toNotificationConfigFromMetaV1(meta);
  const questionnaireConsentConfig = QuestionnaireConsentConfigMetaMapper.toQuestionnaireConsentConfigFromMeta(meta);
  const initiationConfig: Maybe<RequestInitiationConfig> =
    config.type === QuestionnaireType.CLINRO
      ? /** We need to specify that a clinro is self-initiated in the generic
      request properties so that common libs can know whether to expect responses
      for the rolled out request window.*/
        { initiator: RequestInitiatorType.SELF }
      : RequestInitiationConfigMapper.fromRequestInitiationMetaV1(meta);
  const requestGroupConfig = RequestGroupMapper.toRequestGroupMemberships(meta, strict);
  const rpmEventConfig = RpmEventConfigMapper.fromRpmEventConfigMetaV1(meta);
  const tagsConfig = RequestTagsConfigMetaMapper.fromRequestTagsConfigMetaV1(meta);
  const sideEffectConfig = RequestSideEffectConfigMapper.fromRequestSideEffectMetaV1(meta);
  const questionnaireCriticalityConfig =
    QuestionnaireCriticalityConfigMapper.fromQuestionnaireCriticalityConfigMetaV1(meta);

  assertConfig(requestCode, config, timing);
  const request: QuestionnaireRequestInfo = {
    requestCode,
    versionCode,
    scheduleCode,
    questionnaireCode: details?.questionnaireCode ?? '',
    details: details ? QuestionnaireRequestDetails.from(details) : undefined,
    startAction: {
      fixedAction: startAction?.customAction
        ? GenericRequestMapper.customActionDateToFixedAction(startAction?.customAction)
        : GenericRequestMapper.toFixedAction(startAction?.fixedAction),
      // TODO Sebastian: Remove if it is no longer needed by the app
      customAction: startAction?.customAction ? startAction.customAction : undefined,
      offset: startAction?.offset
        ? GenericRequestMapper.toDuration(startAction.offset, SUPPORTED_UNIT_OF_TIMES)
        : undefined,
      fixedTime: startAction?.customAction
        ? GenericRequestMapper.customActionTimeToFixedTime(startAction.customAction)
        : startAction?.fixedTime,
    },
    endAction: {
      fixedAction: endAction?.customAction
        ? GenericRequestMapper.customActionDateToFixedAction(endAction?.customAction)
        : GenericRequestMapper.toFixedAction(endAction?.fixedAction),
      // TODO Sebastian: Remove if it is no longer needed by the app
      customAction: endAction?.customAction ? endAction.customAction : undefined,
      offset: endAction?.offset
        ? GenericRequestMapper.toDuration(endAction.offset, SUPPORTED_UNIT_OF_TIMES)
        : undefined,
      fixedTime: endAction?.customAction
        ? GenericRequestMapper.customActionTimeToFixedTime(endAction.customAction)
        : endAction?.fixedTime,
    },
    config,
    while: GenericRequestMapper.toRequestWhile(requestWhile),
    timing: GenericRequestMapper.toTiming(timing, {
      supportedUnitOfTimes: SUPPORTED_UNIT_OF_TIMES,
    }),
    requestType: evaluateQuestionnaireType(config),
    notificationConfig,
    initiationConfig,
    patientCopingTipConfig,
    questionnaireConsentConfig,
    groups: requestGroupConfig,
    rpmEventConfig,
    tagsConfig,
    sideEffectConfig,
    questionnaireCriticalityConfig,
  };

  ScheduleValidationService.validateRequest(request);
  return request;
};

export const QuestionnaireRequestInfo = {
  from,
};
